import * as styles from './styles.css';
export { styles as utilStyles };
import * as focusStyles_css from './focusStyles.css';
export { focusStyles_css as focusStyles };
import * as caretStyles_css from './caretStyles.css';
export { caretStyles_css as caretStyles };
import { contextColors } from './cssVariables.css';
export { contextColors } from './cssVariables.css';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import { tokens } from '@paypalcorp/pp-react-theme';
import _extends from '@babel/runtime/helpers/esm/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React from 'react';
import _classCallCheck from '@babel/runtime/helpers/esm/classCallCheck';
import _createClass from '@babel/runtime/helpers/esm/createClass';
import _typeof from '@babel/runtime/helpers/esm/typeof';

/**
 * Check whether window object is present or not.
 * @returns boolean
 */
var hasWindow = function () {
  return typeof window !== 'undefined';
};

/**
 * Check whether navigator object is present or not.
 * @returns boolean
 */
/* istanbul ignore next */
var hasNavigator = function () {
  return typeof navigator !== 'undefined';
};

/**
 * Check whether document object is present or not.
 * @returns boolean
 */
/* istanbul ignore next */
var hasDocument = function () {
  return typeof document !== 'undefined';
};

/**
 * Check whether the browser is running in a touch device or not.
 * @returns boolean
 */
/* istanbul ignore next */
var isTouchDevice = function () {
  var _navigator$maxTouchPo;
  if (hasWindow() && 'ontouchstart' in window) {
    return true;
  }
  return !!(hasNavigator() && ( //@ts-ignore
  (_navigator$maxTouchPo = navigator.maxTouchPoints) !== null && _navigator$maxTouchPo !== void 0 ? _navigator$maxTouchPo : navigator.msMaxTouchPoints));
};

/**
 * Check whether the names passed as param is the active element or not.
 * @param elementName Names of the active element. For example `INPUT`, `TEXTAREA`, etc.
 * @returns boolean
 */
/* istanbul ignore next */
var isActiveElementIn = function (elementNames) {
  if (!hasDocument()) {
    return false;
  }
  var activeElement = document.activeElement;
  return activeElement && elementNames.includes(activeElement.tagName);
};

/**
 * Check whether the browser document's direction attribute is `rtl` or not.
 * @returns boolean
 */
/* istanbul ignore next */
var isDocumentRTL = function () {
  return hasWindow() ? getComputedStyle(document.body).direction === 'rtl' : false;
};

var capitalize = function (word) {
  return word && word !== '' ? word[0].toUpperCase() + word.slice(1) : word;
};
var curry = function (func, firstParam) {
  return function () {
    for (var _len = arguments.length, params = new Array(_len), _key = 0; _key < _len; _key++) {
      params[_key] = arguments[_key];
    }
    return func.apply(void 0, [firstParam].concat(params));
  };
};
var getPrefixedAttribute = function (prefix, name, object, fallback) {
  var _ref, _object$name;
  return (_ref = (_object$name = object[name]) !== null && _object$name !== void 0 ? _object$name : object["".concat(prefix).concat(name)]) !== null && _ref !== void 0 ? _ref : typeof fallback === 'function' ? fallback(prefix, name, object) : fallback;
};
var VARIABLE_PATTERN = /\${(\w+)}/g;
var tokenTemplate = function (template, tokenMap) {
  return template.replace(VARIABLE_PATTERN, function (_match, name) {
    var _tokenMap$name;
    return (_tokenMap$name = tokenMap[name]) !== null && _tokenMap$name !== void 0 ? _tokenMap$name : "${".concat(name, "}");
  });
};

var PREFIX_COLOR = 'sysColor';
var getColor = curry(getPrefixedAttribute, PREFIX_COLOR);
var getColorFamily = function (name) {
  return tokens.color[name];
};
var standardFamily = {
  fore: 'contrast',
  back: 'main',
  hover: 'mainHover',
  active: 'mainActive'
};

/*
 * Instead of crazy "ifs" and conditionals to get around the unfortunate
 * inconsistencies in how we named the tokens, let's just map the exceptions.
 * This gives us better control.
 *
 * Most of the entries are consistent so they get the 'standardFamily'
 *
 * familyName is assumed to be the same as the key we used to index each
 * item. Some of these families aren't named directly after the token
 * or are variants of a token that's used in other ways. So we allow
 * the family name to differ from its index. (see primary and primaryInverse)
 * A "standardFamily" omits the family name.
 */
var colorFamilyVariations = {
  main: {
    familyName: 'background',
    fore: 'mediumContrast',
    back: 'main'
  },
  contrast: {
    familyName: 'background',
    fore: 'highContrast',
    back: 'main'
  },
  surface: {
    familyName: 'background',
    fore: 'mediumContrast',
    back: 'surface',
    hover: 'surfaceHover',
    active: 'surfaceActive'
  },
  primary: standardFamily,
  primaryInverse: {
    familyName: 'primary',
    fore: 'main',
    back: 'contrast',
    hover: 'contrastHover',
    active: 'contrastActive'
  },
  secondary: standardFamily,
  tertiary: standardFamily,
  featured: standardFamily,
  unselected: standardFamily,
  selected: standardFamily,
  linkMain: standardFamily,
  linkAlt: standardFamily,
  overlay: standardFamily,
  accent1: standardFamily,
  accent2: standardFamily,
  accent3: standardFamily,
  accent4: standardFamily,
  error: standardFamily,
  warning: standardFamily,
  success: standardFamily,
  system: standardFamily,
  info: standardFamily,
  specialty: standardFamily
};
/*
 * Use this method in a container (Alert?) to set the color context for its children
 * It will return a section you can use in the 'vars' section of your style block.
 * So for a box with the "success" color family you would set:
 *
 *    :::
 *    vars: getColorFamilyVars(tokens, 'success')
 *    :::
 *
 * There are some specially named families that make alternate use of the tokens.
 *
 * "main" is the standard off-white with medium contrast
 * "contrast" is the same but with higher contrast text
 * "surface" uses the proper colors for hover and active
 * "primaryInverse" takes the place of (or implements) primary + inverse
 */
var getColorFamilyVars = function (tokens) {
  var family = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'main';
  var _colorFamilyVariation = colorFamilyVariations[family],
    _colorFamilyVariation2 = _colorFamilyVariation.familyName,
    familyName = _colorFamilyVariation2 === void 0 ? family : _colorFamilyVariation2,
    fore = _colorFamilyVariation.fore,
    back = _colorFamilyVariation.back,
    hover = _colorFamilyVariation.hover,
    active = _colorFamilyVariation.active;
  var color = tokens.color;
  var colorEntry = color[familyName];
  return _defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty({}, contextColors.main, colorEntry[fore]), contextColors.background, colorEntry[back]), contextColors.hoverMain, colorEntry[fore]), contextColors.hoverBack, colorEntry[hover]), contextColors.activeMain, colorEntry[fore]), contextColors.activeBack, colorEntry[active]);
};

var AnimateDirection = /*#__PURE__*/function (AnimateDirection) {
  AnimateDirection["LEFT"] = "left";
  AnimateDirection["RIGHT"] = "right";
  return AnimateDirection;
}({});
var ZINDEX_DROPDOWN = 1060;
var ZINDEX_TOOLTIP = 1070;
var ZINDEX_COACHTIP = 1080;
var ZINDEX_MODAL = 1090; // cookiebanner z-index is 1051
var ZINDEX_TOAST = 1100; // can be on top of modal

var DEFAULT_CDNPATH = 'www.paypalobjects.com';
var DEFAULT_ASSETPATH = 'icons/v5'; // tomorrow it will be illustrations, ...
var svgBasePath = function () {
  var cdnHost = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : DEFAULT_CDNPATH;
  var assetPath = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : DEFAULT_ASSETPATH;
  return "https://".concat(cdnHost, "/paypal-ui/").concat(assetPath, "/svg");
};

var UNIT_PARSER = /([+-]?\d*\.?\d*)([a-z%]*)?/;
var splitValue = function (str, defaultUnit) {
  var _ref;
  if (typeof str === 'number') {
    return {
      unit: defaultUnit,
      value: str
    };
  }
  var parts = str.match(UNIT_PARSER);
  var value = parseFloat(str);
  return {
    value: value,
    unit: (_ref = parts && parts[2]) !== null && _ref !== void 0 ? _ref : defaultUnit
  };
};
var getMs = function (str) {
  var _splitValue = splitValue(str, 'ms'),
    unit = _splitValue.unit,
    value = _splitValue.value;
  return unit === 's' ? value * 1000 : value;
};
var getPx = function (str) {
  var remBase = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 16;
  var _splitValue2 = splitValue(str, 'px'),
    unit = _splitValue2.unit,
    value = _splitValue2.value;
  return unit === 'rem' ? value * remBase : value;
};
var getRem = function (str) {
  var remBase = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 16;
  var _splitValue3 = splitValue(str, 'rem'),
    unit = _splitValue3.unit,
    value = _splitValue3.value;
  return unit === 'px' ? value / remBase : value;
};

var cx = function () {
  var className = '';
  for (var _len = arguments.length, names = new Array(_len), _key = 0; _key < _len; _key++) {
    names[_key] = arguments[_key];
  }
  names.forEach(function (name, i) {
    if (!!name && typeof name === 'string') className += i === 0 ? "".concat(name) : " ".concat(name);
  });
  return className;
};
var rgba = function (tokenName, opacityPercent) {
  return "color-mix(in srgb, ".concat(tokenName, " ").concat(opacityPercent, ", transparent)");
};
var remToPx = function (num) {
  var base = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 16;
  return num * base;
};

var _excluded$1 = ["className"],
  _excluded2 = ["className"];
var withAnimation = function (WrappedComponent) {
  var WCAnimation = function (_ref) {
    var className = _ref.className,
      rest = _objectWithoutProperties(_ref, _excluded$1);
    return /*#__PURE__*/React.createElement(WrappedComponent, _extends({
      className: "".concat(styles.animationTimingName, " ").concat(className)
    }, rest));
  };
  WCAnimation.displayName = 'withAnimation';
  return WCAnimation;
};
var withTransition = function (WrappedComponent) {
  var WCTransition = function (_ref2) {
    var className = _ref2.className,
      rest = _objectWithoutProperties(_ref2, _excluded2);
    return /*#__PURE__*/React.createElement(WrappedComponent, _extends({
      className: "".concat(styles.transitionTimingName, " ").concat(className)
    }, rest));
  };
  WCTransition.displayName = 'withTransition';
  return WCTransition;
};

var PREFIX_DEFAULT = 'ppui';
var ppuiUniqueIdCounter = {};

/**
 * Generates an unique ID.
 * If prefix is passed, the function prepends the prefix with the ID.
 *
 * @param {string} prefix The value to prefix with the ID.
 * @returns {string} The unique ID.
 */
function uniqueId() {
  var prefix = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : PREFIX_DEFAULT;
  if (!ppuiUniqueIdCounter[prefix]) {
    ppuiUniqueIdCounter[prefix] = 0;
  }
  var id = ++ppuiUniqueIdCounter[prefix];
  return prefix === PREFIX_DEFAULT ? "".concat(id) : "".concat(prefix).concat(id);
}

var KeyboardCode = /*#__PURE__*/function () {
  function KeyboardCode(code) {
    _classCallCheck(this, KeyboardCode);
    _defineProperty(this, "key", void 0);
    this.key = code;
  }
  return _createClass(KeyboardCode, [{
    key: "IS_ARROW_LEFT",
    get: function get() {
      return ['ArrowLeft', KeyboardCode.NAME_ARROW_LEFT, KeyboardCode.CODE_ARROW_LEFT].includes(this.key);
    }
  }, {
    key: "IS_ARROW_RIGHT",
    get: function get() {
      return ['ArrowRight', KeyboardCode.NAME_ARROW_RIGHT, KeyboardCode.CODE_ARROW_RIGHT].includes(this.key);
    }
  }, {
    key: "IS_ARROW_UP",
    get: function get() {
      return ['ArrowUp', KeyboardCode.NAME_ARROW_UP, KeyboardCode.CODE_ARROW_UP].includes(this.key);
    }
  }, {
    key: "IS_ARROW_DOWN",
    get: function get() {
      return ['ArrowDown', KeyboardCode.NAME_ARROW_DOWN, KeyboardCode.CODE_ARROW_DOWN].includes(this.key);
    }
  }, {
    key: "IS_ENTER",
    get: function get() {
      return [KeyboardCode.NAME_ENTER, KeyboardCode.CODE_ENTER].includes(this.key);
    }
  }, {
    key: "IS_SPACEBAR",
    get: function get() {
      return [KeyboardCode.NAME_SPACE, KeyboardCode.CODE_SPACE].includes(this.key);
    }
  }, {
    key: "IS_TAB",
    get: function get() {
      return [KeyboardCode.NAME_TAB, KeyboardCode.CODE_TAB].includes(this.key);
    }
  }, {
    key: "IS_ESC",
    get: function get() {
      return ['Escape', KeyboardCode.NAME_ESC, KeyboardCode.CODE_ESC].includes(this.key);
    }
  }, {
    key: "IS_HOME",
    get: function get() {
      return [KeyboardCode.NAME_HOME, KeyboardCode.CODE_HOME].includes(this.key);
    }
  }, {
    key: "IS_END",
    get: function get() {
      return [KeyboardCode.NAME_END, KeyboardCode.CODE_END].includes(this.key);
    }
  }, {
    key: "IS_BACKSPACE",
    get: function get() {
      return [KeyboardCode.NAME_BACKSPACE, KeyboardCode.CODE_BACKSPACE].includes(this.key);
    }
  }, {
    key: "IS_DELETE",
    get: function get() {
      return [KeyboardCode.NAME_DELETE, KeyboardCode.CODE_DELETE].includes(this.key);
    }
  }, {
    key: "IS_SIDE_NAVIGATION",
    get: function get() {
      return this.IS_ARROW_LEFT || this.IS_ARROW_RIGHT || this.IS_END || this.IS_HOME;
    }
  }, {
    key: "IS_ARROW_KEY",
    get: function get() {
      return this.IS_ARROW_LEFT || this.IS_ARROW_RIGHT || this.IS_ARROW_UP || this.IS_ARROW_DOWN;
    }
  }]);
}();
_defineProperty(KeyboardCode, "CODE_ARROW_UP", 38);
_defineProperty(KeyboardCode, "CODE_ARROW_DOWN", 40);
_defineProperty(KeyboardCode, "CODE_ARROW_LEFT", 37);
_defineProperty(KeyboardCode, "CODE_ARROW_RIGHT", 39);
_defineProperty(KeyboardCode, "CODE_ENTER", 13);
_defineProperty(KeyboardCode, "CODE_SPACE", 32);
_defineProperty(KeyboardCode, "CODE_ESC", 27);
_defineProperty(KeyboardCode, "CODE_TAB", 9);
_defineProperty(KeyboardCode, "CODE_CAPS_LOCK", 20);
_defineProperty(KeyboardCode, "CODE_HOME", 36);
_defineProperty(KeyboardCode, "CODE_END", 35);
_defineProperty(KeyboardCode, "CODE_BACKSPACE", 8);
_defineProperty(KeyboardCode, "CODE_DELETE", 46);
_defineProperty(KeyboardCode, "NAME_ARROW_UP", 'Up');
_defineProperty(KeyboardCode, "NAME_ARROW_DOWN", 'Down');
_defineProperty(KeyboardCode, "NAME_ARROW_LEFT", 'Left');
_defineProperty(KeyboardCode, "NAME_ARROW_RIGHT", 'Right');
_defineProperty(KeyboardCode, "NAME_ENTER", 'Enter');
_defineProperty(KeyboardCode, "NAME_SPACE", ' ');
_defineProperty(KeyboardCode, "NAME_ESC", 'Esc');
_defineProperty(KeyboardCode, "NAME_TAB", 'Tab');
_defineProperty(KeyboardCode, "NAME_HOME", 'Home');
_defineProperty(KeyboardCode, "NAME_END", 'End');
_defineProperty(KeyboardCode, "NAME_BACKSPACE", 'Backspace');
_defineProperty(KeyboardCode, "NAME_DELETE", 'Delete');

var _excluded = ["as", "children", "className"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var deepClone = function (obj) {
  if (obj === null || _typeof(obj) !== 'object') {
    return obj;
  }
  var temp = new obj.constructor();
  for (var key in obj) {
    // istanbul ignore next
    if (obj.hasOwnProperty(key)) {
      temp[key] = deepClone(obj[key]);
    }
  }
  return temp;
};

/**
 * Returns JSX to render suggestion with highlighted match
 * @param src
 * @param match
 */
var highlightMatch = function (src, match) {
  if (match) {
    var escapedMatch = match.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
    var regex = new RegExp(escapedMatch, 'ig');
    return src.replace(regex, '<strong>$&</strong>');
  }
  return src;
};

/**
 * throttle function - limiting the number of times
 * a function gets called in a certain time period.
 * @param callback (Function)
 * @param delay (milliseconds)
 */
/* istanbul ignore next */
var throttle = function (callback, delay) {
  var timer = 0;
  return function () {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    clearTimeout(timer);
    timer = setTimeout(function () {
      return callback.apply(null, args);
    }, delay);
  };
};
var IfNotEmpty = function (_ref) {
  var _ref$as = _ref.as,
    as = _ref$as === void 0 ? 'span' : _ref$as,
    children = _ref.children,
    className = _ref.className,
    rest = _objectWithoutProperties(_ref, _excluded);
  return children ? React.createElement(as, _objectSpread({
    className: className
  }, rest), children) : null;
};

// istanbul ignore next
var devWarning = function (warningText) {
  if (process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'ppui-test') {
    // eslint-disable-next-line no-console
    console.warn(warningText);
  }
};

// istanbul ignore next
var getThemeClassName = function () {
  var _document$getElements;
  var name = hasDocument() && ((_document$getElements = document.getElementsByClassName('ppui-theme')[0]) === null || _document$getElements === void 0 ? void 0 : _document$getElements.classList[1]);
  if (!name) {
    devWarning('pp-react theme classname not found. Make sure you have integrated `ThemeWrapper` from `@paypalcorp/pp-react`');
    return '';
  }
  return name;
};

export { AnimateDirection, DEFAULT_ASSETPATH, DEFAULT_CDNPATH, IfNotEmpty, KeyboardCode, PREFIX_COLOR, ZINDEX_COACHTIP, ZINDEX_DROPDOWN, ZINDEX_MODAL, ZINDEX_TOAST, ZINDEX_TOOLTIP, capitalize, colorFamilyVariations, curry, cx, deepClone, devWarning, getColor, getColorFamily, getColorFamilyVars, getMs, getPrefixedAttribute, getPx, getRem, getThemeClassName, hasDocument, hasNavigator, hasWindow, highlightMatch, isActiveElementIn, isDocumentRTL, isTouchDevice, remToPx, rgba, splitValue, svgBasePath, throttle, tokenTemplate, uniqueId, withAnimation, withTransition };
